import { useEffect, useMemo, useRef, useState } from 'react'
import { X, Send, MessageCircle, Loader2 } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { useOrderMessages, type OrderMessage } from '../hooks/useOrderMessages'
import { api } from '../services/api'
import { showToast } from './Toast'

interface Props {
  open: boolean
  orderId: string | null
  /** Viewer's role — drives which side's bubbles align right, which API
   *  endpoint to call, and which title text to show. */
  role: 'customer' | 'owner'
  /** Disable the input when the order is terminal (delivered/cancelled).
   *  History is still visible. */
  closedForWrite: boolean
  /** Customer auth: pass the user_id query param. Owner auth: pass a
   *  Firebase ID token getter (because it's async). The component will
   *  pick whichever matches `role`. */
  userId?: string | null
  getOwnerIdToken?: () => Promise<string | null>
  /** For the owner-side notification label (shown in the sheet header). */
  storeNameForCustomer?: string
  onClose: () => void
}

/**
 * #338 in-order chat sheet. Bottom sheet overlay that shows the full
 * message history for a single order and lets the viewer send a reply.
 *
 * Reuses the `max-w-[430px] rounded-t-2xl animate-slide-up` shell from
 * CartSheet. Messages come from `useOrderMessages`, which **#1025 で
 * onSnapshot 直読から 3 秒ポーリングに変えた**（rules を deny にして全店の
 * チャット本文が公開スクレイプできる状態を塞いだため）。開いている間だけ回る。
 *
 * On mount (when `open` and `orderId` are both set), marks the viewer's
 * unread counter as zero via the API — this is what the badge hook reads
 * to drop the red dot. We also reset on every new fetch so that
 * incoming messages received *while the sheet is open* don't stack up.
 */
export default function OrderChatSheet({
  open, orderId, role, closedForWrite, userId, getOwnerIdToken, storeNameForCustomer, onClose,
}: Props) {
  const { t } = useI18n()
  const { messages, loading, refetch } = useOrderMessages(open ? orderId : null, { role, userId })
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  // Auto-scroll to bottom on new messages or when opening with existing history.
  useEffect(() => {
    if (!scrollRef.current) return
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [messages.length, open])

  // Mark as read on open + whenever a new message comes in while open.
  // We call the API unconditionally; the backend is idempotent (setting a
  // counter to 0 that's already 0 is harmless).
  useEffect(() => {
    if (!open || !orderId) return
    ;(async () => {
      try {
        if (role === 'customer' && userId) {
          await api.markOrderMessagesRead(orderId, userId)
        } else if (role === 'owner' && getOwnerIdToken) {
          const idToken = await getOwnerIdToken()
          if (idToken) await api.markOwnerOrderMessagesRead(orderId, idToken)
        }
      } catch (e) {
        console.error('[OrderChatSheet] markRead failed:', e)
      }
    })()
  }, [open, orderId, role, userId, getOwnerIdToken, messages.length])

  const canSend = useMemo(
    () => !closedForWrite && !sending && draft.trim().length > 0,
    [closedForWrite, sending, draft],
  )

  const handleSend = async () => {
    if (!canSend || !orderId) return
    const body = draft.trim()
    setSending(true)
    try {
      if (role === 'customer') {
        if (!userId) throw new Error('Missing userId')
        const res = await api.sendOrderMessage(orderId, body, userId)
        if (res?.detail) throw new Error(res.detail)
      } else {
        if (!getOwnerIdToken) throw new Error('Missing owner token getter')
        const idToken = await getOwnerIdToken()
        if (!idToken) throw new Error('Missing ID token')
        const res = await api.sendOwnerOrderMessage(orderId, body, idToken)
        if (res?.detail) throw new Error(res.detail)
      }
      setDraft('')
      // #1025 直読を止めてポーリングにしたので、送信直後だけは自分で取り直す。
      // これが無いと自分の吹き出しが最大 3 秒出てこない。
      refetch()
    } catch (e) {
      console.error('[OrderChatSheet] send failed:', e)
      showToast({ type: 'error', message: t('chat.sendFailed') })
    } finally {
      setSending(false)
    }
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter = send; Shift+Enter = newline. Match Slack / Discord convention.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (!open) return null

  const title = role === 'customer' ? t('chat.titleForCustomer') : t('chat.titleForOwner')

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[430px] bg-white rounded-t-2xl flex flex-col animate-slide-up h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <MessageCircle size={18} className="text-matcha" />
            <h3 className="font-display font-semibold text-base text-text-primary">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
            aria-label="close"
          >
            <X size={18} className="text-text-secondary" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-gray-50">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={20} className="animate-spin text-text-tertiary" />
            </div>
          )}
          {!loading && messages.length === 0 && (
            <p className="text-center text-xs font-body text-text-tertiary py-8">
              {t('chat.empty')}
            </p>
          )}
          {messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              mine={(role === 'customer' && m.senderType === 'customer')
                || (role === 'owner' && m.senderType === 'owner')}
              storeLabel={storeNameForCustomer || t('chat.storeLabel')}
              youLabel={t('chat.youLabel')}
              customerLabel={t('chat.customerLabel')}
            />
          ))}
        </div>

        <div className="border-t border-gray-100 px-4 py-3 shrink-0">
          {closedForWrite ? (
            <p className="text-xs font-body text-text-tertiary text-center py-2">
              {t('chat.closedForWrite')}
            </p>
          ) : (
            <div className="flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t('chat.placeholder')}
                rows={2}
                className="flex-1 bg-gray-50 rounded-lg px-3 py-2 font-body text-sm text-text-primary outline-none focus:ring-2 focus:ring-yokoso-red/30 resize-none"
                disabled={sending}
              />
              <button
                onClick={handleSend}
                disabled={!canSend}
                className={`h-10 px-4 rounded-lg flex items-center gap-1 font-display text-sm font-semibold ${
                  canSend ? 'bg-yokoso-red text-white tap-effect' : 'bg-gray-300 text-white cursor-not-allowed'
                }`}
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                <span>{sending ? t('chat.sending') : t('chat.send')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function MessageBubble({
  message, mine, storeLabel, youLabel, customerLabel,
}: {
  message: OrderMessage
  mine: boolean
  storeLabel: string
  youLabel: string
  customerLabel: string
}) {
  const senderLabel = mine
    ? youLabel
    : message.senderType === 'owner' ? storeLabel : customerLabel
  const time = message.createdAt
    ? new Date(message.createdAt).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })
    : ''
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[78%] ${mine ? 'items-end' : 'items-start'} flex flex-col gap-0.5`}>
        <span className="text-[10px] font-body text-text-tertiary px-1">
          {senderLabel}{time ? ` · ${time}` : ''}
        </span>
        <div
          className={`px-3 py-2 rounded-2xl font-body text-sm whitespace-pre-wrap break-words ${
            mine
              ? 'bg-yokoso-red text-white rounded-br-sm'
              : 'bg-white border border-gray-200 text-text-primary rounded-bl-sm'
          }`}
        >
          {message.body}
        </div>
      </div>
    </div>
  )
}
